import { observable, action, computed } from 'mobx'
import { has } from 'lodash'
import { IPrismaClientRequestRPC } from '@prisma/studio-types'

import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import FieldStore from 'stores/field/FieldStore'
import Field from 'stores/field/Field'

import createError from 'utils/createError'
import getCountQuery from 'utils/getCountQuery'

export interface IModelCreatePayload {
  id: string
  dbName: string | null
  name: string
  plural: string
  fieldIds: string[]
  idFieldIds: string[]
  uniqueFieldIds: string[]
}

type IModelUpdatePayload = Partial<IModelCreatePayload> &
  Partial<{
    count: number
  }>

class Model extends BaseListItem {
  @observable id: IModelCreatePayload['id']
  @observable dbName: IModelCreatePayload['dbName']
  @observable name: IModelCreatePayload['name']
  @observable plural: IModelCreatePayload['plural']
  @observable count: number | null
  @observable fieldIds: IModelCreatePayload['fieldIds']
  @observable private idFieldIds: IModelCreatePayload['idFieldIds'] // Do not use this unless you know what you're doing
  @observable private uniqueFieldIds: IModelCreatePayload['uniqueFieldIds'] // Do not use this unless you know what you're doing

  constructor(model: IModelCreatePayload) {
    super(model)

    this.id = model.id
    this.dbName = model.dbName
    this.name = model.name
    this.plural = model.plural
    this.count = null
    this.fieldIds = model.fieldIds || []
    this.idFieldIds = model.idFieldIds || []
    this.uniqueFieldIds = model.uniqueFieldIds || []
  }

  @computed
  get fields(): Field[] {
    return this.fieldIds.map(fId => {
      const field = FieldStore.get(fId)
      if (!field) {
        throw createError('Model.field', 'Invalid fieldId', { fieldId: fId })
      }

      return field
    })
  }

  @computed
  get uniqueIdentifiers(): Field[] {
    // Unique fields for a model can be one of (in this order):
    // 1. A field with an @id annotation
    // 2. Multiple fields with @@id clause
    // 3. One (and only one) field with a @unique annotation (if there are multiple, use the first one)
    // 4. Multiple fields with a @@unique clause

    // Try finding 1.
    if (this.idField) {
      return [this.idField]
    }

    // Try finding 2.
    if (this.compoundIdFields.length > 0) {
      return this.compoundIdFields
    }

    // Try finding 3.
    const singleUniqueField = this.uniqueFields && this.uniqueFields[0]
    if (singleUniqueField) {
      return [singleUniqueField]
    }

    // Try finding 4.
    if (this.compoundUniqueFields.length > 0) {
      return this.compoundUniqueFields
    }

    throw createError(
      'ModelStore.uniqueIdentifiers',
      'Unable to resolve unique identifiers for model',
      { modelId: this.id }
    )
  }

  @computed
  get idField(): Field | null {
    // Unless you specifically need the ID field, prefer to use `uniqueIdentifiers` instead
    return this.fields.find(f => f.isId) || null
  }

  @computed
  // Unless you specifically need the unique fields, prefer to use `uniqueIdentifiers` instead
  get uniqueFields(): Field[] {
    return this.fields.filter(f => f.isUnique)
  }

  @computed
  get compoundIdFields(): Field[] {
    // Unless you specifically need the compound ID fields, prefer to use `uniqueIdentifiers` instead
    return this.idFieldIds.map(fId => {
      const field = FieldStore.get(fId)
      if (!field) {
        throw createError(
          'Model.compoundIdFields',
          'Invalid fieldId in idFieldIds',
          {
            fieldId: fId,
          }
        )
      }

      return field
    })
  }

  @computed
  get compoundUniqueFields(): Field[] {
    // Unless you specifically need the compound unique fields, prefer to use `uniqueIdentifiers` instead
    return this.uniqueFieldIds.map(fId => {
      const field = FieldStore.get(fId)
      if (!field) {
        throw createError(
          'Model.uniqueIdentifiers',
          'Invalid fieldId in uniqueFieldIds',
          { fieldId: fId }
        )
      }

      return field
    })
  }

  @computed
  get photonNamespace(): string {
    // Gets the `something` to put in `prisma.<something>.findMany/create` etc. requests
    return this.name.charAt(0).toLowerCase() + this.name.slice(1)
  }

  getFieldByName = (fieldName: string) => {
    return FieldStore.getByName(fieldName, this.id)
  }

  @action
  runCountQuery = async () => {
    try {
      const {
        error,
        data,
      } = await window.transport.request<IPrismaClientRequestRPC>({
        channel: 'prisma',
        action: 'clientRequest',
        payload: {
          data: {
            query: getCountQuery({ modelId: this.id }),
          },
        },
      })

      if (error) {
        throw error
      }

      const count = parseInt(data.response)
      this.update({ count })
    } catch (e) {
      // If this request fails, it is not a big deal
      console.log('Count request failed for model:', this.name, e)
      this.update({ count: 0 })
    }
  }

  @action
  update(model: IModelUpdatePayload, options: IBaseListItemUpdateOptions = {}) {
    has(model, 'name') && (this.name = model.name!)
    has(model, 'plural') && (this.plural = model.plural!)
    has(model, 'count') && (this.count = model.count!)
    has(model, 'fieldIds') && (this.fieldIds = model.fieldIds!)
    has(model, 'idFieldIds') && (this.idFieldIds = model.idFieldIds!)
    has(model, 'uniqueFieldIds') &&
      (this.uniqueFieldIds = model.uniqueFieldIds!)

    super.update(model, options)
  }
}

export default Model
